// Adoption of a generated policy.
//
// A policy the platform writes is a draft: it says what this organisation could
// commit to, not what it has committed to. Until someone reviews it and puts
// their copy where the organisation keeps its evidence, the compliance check
// ignores it — so the card says that plainly, and gives the two ways to fix it:
// upload the reviewed copy here, or edit the copy already sitting in the
// connected storage the platform reads.
//
// Backend contract: GET /templates/adoption (backend/src/lib/policyAdoption.ts).

import { useRef, useState } from "react";
import { theme } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { api } from "../api/client";
import { fmtDateOnly } from "../lib/dateOnly";

export type PolicyAdoptionState = "adopted" | "adopted_superseded" | "draft_only";

export interface PolicyAdoptionRow {
  templateId: string;
  title: string;
  state: PolicyAdoptionState;
  generated: { versionNumber: number; generatedAt: string; stale: boolean };
  adopted?: { evidenceDocumentId: string; fileName: string; adoptedAt: string; source: string };
  note?: string;
}

const CHIP: Record<PolicyAdoptionState, { label: string; tone: StatusTone }> = {
  adopted:            { label: "Adopted",          tone: "met" },
  adopted_superseded: { label: "Adopted · behind", tone: "partial" },
  draft_only:         { label: "Draft — not adopted", tone: "partial" },
};

export function AdoptionChip({ state }: { state: PolicyAdoptionState }) {
  const c = CHIP[state];
  return <StatusTag tone={c.tone}>{c.label}</StatusTag>;
}

const ADOPTION_HELP =
  "A generated policy is a draft. Compliance checks assess it only once someone here has reviewed it and the reviewed copy is in the Evidence Repository — upload it below, or edit the copy in your connected storage and it will be picked up automatically.";

interface Props {
  templateId: string;
  /** The row from GET /templates/adoption, when the policy has been generated. */
  row?: PolicyAdoptionRow;
  canWrite: boolean;
  onAdopted: (row: PolicyAdoptionRow) => void;
}

export function PolicyAdoptionPanel({ templateId, row, canWrite, onAdopted }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const state: PolicyAdoptionState = row?.state ?? "draft_only";
  const adopted = row?.adopted;

  async function upload(file: File) {
    setBusy(true);
    setError("");
    try {
      const contentType = file.type || "application/octet-stream";
      const { uploadUrl, blobPath } = await api.post<{ uploadUrl: string; blobPath: string }>(
        "/documents/request-upload",
        { fileName: file.name, contentType },
      );
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": contentType },
        body: file,
      }).catch(() => null);
      if (!put?.ok) throw new Error("Upload to storage failed — check your connection and try again.");

      await api.post("/documents", {
        blobPath,
        fileName: file.name,
        sizeBytes: file.size,
        tags: [],
        policyTemplateId: templateId,
      });

      const fresh = await api.get<{ policies: PolicyAdoptionRow[] }>("/templates/adoption");
      const mine = fresh.policies.find((p) => p.templateId === templateId);
      if (mine) onAdopted(mine);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not adopt this policy.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const tone = state === "adopted" ? theme.color.success : theme.color.warning;

  return (
    <div
      style={{
        border: `1px solid ${tone}`, background: tone + "14", borderRadius: 3,
        padding: "10px 12px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <AdoptionChip state={state} />
        <span style={{ fontSize: 11, color: theme.color.subtle, lineHeight: 1.5 }}>
          {row?.note ??
            (adopted
              ? `Adopted ${fmtDateOnly(adopted.adoptedAt)} — assessed as this organisation's own policy.`
              : "Not assessed until a reviewed copy is on file.")}
        </span>
      </div>

      {state !== "adopted" && (
        <div style={{ fontSize: 11, color: theme.color.subtle, lineHeight: 1.6 }}>{ADOPTION_HELP}</div>
      )}

      {canWrite && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input
            ref={fileRef}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
          />
          <button
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            style={{
              padding: "5px 12px", borderRadius: 3, fontSize: 11, fontWeight: 700,
              cursor: busy ? "default" : "pointer",
              border: `1px solid ${theme.color.accent}`,
              background: theme.color.accentBg, color: theme.color.accentText, opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Uploading…" : state === "adopted" ? "Replace adopted copy" : "Upload reviewed copy"}
          </button>
          {adopted && (
            <span style={{ fontSize: 10, color: theme.color.faint }}>On file: {adopted.fileName}</span>
          )}
        </div>
      )}

      {error && <div style={{ fontSize: 11, color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}
